import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const ViewModeContext = createContext();

export const ViewModeProvider = ({ children }) => {
  const [viewMode, setViewMode] = useState(() => {
    // Initialize from localStorage or default to 'deals'
    const savedMode = localStorage.getItem("noble-view-mode");
    return savedMode || "deals";
  });

  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("noble-view-mode", viewMode);
  }, [viewMode]);

  // Initialize view mode based on current URL
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/portfolio")) {
      if (viewMode !== "portfolio") {
        setViewMode("portfolio");
      }
    } else if (path.startsWith("/deals")) {
      if (viewMode !== "deals") {
        setViewMode("deals");
      }
    }
  }, [location.pathname]);

  const changeViewMode = async (newMode) => {
    if (newMode === viewMode || isTransitioning) return;

    setIsTransitioning(true);

    try {
      // First, give React time to unmount components with the old view mode
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Clear any existing selections when switching modes
      setViewMode(newMode);

      // Navigate to the appropriate base route
      if (newMode === "portfolio") {
        navigate("/portfolio");
      } else {
        navigate("/deals");
      }

      // Give time for new components to mount before clearing transition state
      await new Promise((resolve) => setTimeout(resolve, 200));
    } catch (error) {
      console.error("Error changing view mode:", error);
    } finally {
      setIsTransitioning(false);
    }
  };

  const value = {
    viewMode,
    isTransitioning,
    changeViewMode,
    isPortfolioMode: viewMode === "portfolio",
    isDealsMode: viewMode === "deals",
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error("useViewMode must be used within a ViewModeProvider");
  }
  return context;
};
